<template>
  <Loader :value="promises.fetchOne" :message="$metaInfo.title" class="form-submit-mutate">
    <div class="container view">
      <HeaderComponent :title="$metaInfo.title" :breadcrumb="breadcrumbs">
        <template #right>
          <ButtonComponent
            v-if="$store.getters.hasAccess('admin')"
            :label="$t('delete')"
            @click="removeDialog = true"
            :icon="['fal', 'times']"
          />
        </template>
      </HeaderComponent>

      <div class="form-submit-mutate__content">
        <Card v-if="item.form" variant="prompt">
          <template #header>
            <h2>
              {{ item.form.title }}
            </h2>
            <ButtonComponent
              v-if="$path('data.lead', item)"
              :label="$t('gotoItem', [$tc('lead', 1)])"
              theme="secondary"
              tag="router-link"
              :to="{
                name: `${formReference.onModel.toLowerCase()}-lead`,
                params: { id: $path('data.lead', item), reference: formReference.id },
              }"
              :icon="['fal', 'arrow-right']"
            />
          </template>
          <template #default>
            <div class="form-submit-mutate__items" v-if="columns.length">
              <div v-for="col in columns" :key="col.key" class="form-submit-mutate__item">
                <div>{{ col.label }}</div>

                <div>
                  <FileComponent
                    v-if="$path(`content.size`, col)"
                    class="text--small"
                    :value="$path(`content`, col)"
                    readonly
                  />

                  <div v-else-if="col.content && typeof col.content === 'string' && col.content.startsWith('https')">
                    <a target="_blank" :href="col.content"> {{ col.content }}</a>
                  </div>
                  <div v-else>{{ col.content }}</div>
                </div>
              </div>
            </div>
          </template>
        </Card>
      </div>
      <RemoveComponent
        :show.sync="removeDialog"
        :title="$metaInfo.title"
        :action="() => remove({ id })"
        @onSuccess="
          $router.push({
            name: `form-submits`,
            params: { id: $path('form.id', item) },
          })
        "
        :resource="$tc('lead')"
      />
    </div>
  </Loader>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import { mapi18n } from '@/i18n'
import Card from '@/components/Card-v2'
import RemoveComponent from '@/components/Remove'
import HeaderComponent from '@/components/Header'
import DataTable from '@/components/DataTable'
import Badge from '@/components/Badge'
import { FileComponent } from '@kvass/file'

const hideFields = ['lead', 'privacy', 'policy', 'header', 'scopes', 'referrer']
const LabelMap = {
  'contact.name': 'name',
  'contact.email': 'email',
  'contact.phone': 'phone',
}
export default {
  props: {
    id: String,
  },
  data() {
    return {
      removeDialog: false,
      commentPromise: null,
    }
  },
  watch: {
    id: {
      handler(id = '') {
        return this.fetch({ id })
      },
      immediate: true,
    },
  },
  computed: {
    ...mapState('FormSubmit', ['item', 'promises']),
    ...mapi18n('labels', ['email', 'name', 'phone']),
    ...mapi18n('i18n', {
      loading() {
        return this.$tc('loading') + '...'
      },
    }),

    formReference() {
      return this.$path('form.reference', this.item)
    },
    fields() {
      return (this.item.form.fields || []).map(i => {
        return {
          id: i.key,
          label: i.label,
        }
      })
    },
    columns() {
      const data = this.item.data || {}

      const toColumn = (key, label) => ({
        key,
        label: this.$tc(LabelMap[key] || label || key),
        content: data[key],
      })

      return (this.item.form.fields || [])
        .map(field => {
          if (field.component === 'lead')
            return [
              toColumn('contact.name', this.$t('name')),
              toColumn('contact.email', this.$t('email')),
              toColumn('contact.phone', this.$t('phone')),
              toColumn('comment', this.$tc('message', 1)),
            ]

          if (field.component === 'position')
            return [toColumn('address', this.$t('address')), toColumn('placement', this.$t('placement'))]

          if (hideFields.includes(field.key) || hideFields.includes(field.component)) return

          const columns = [toColumn(field.key, field.label)]

          return columns
        })
        .flat()
        .filter(col => col && col.content !== undefined)
    },

    breadcrumbs() {
      return [
        {
          title: this.$path('name', this.formReference),
          action: {
            name: `${(this.$path('onModel', this.formReference) || '').toLowerCase()}-dashboard`,
            params: { id: this.$path('id', this.formReference) },
          },
        },
        {
          title: this.$path('form.title', this.item),
          action: {
            name: `form-submits`,
            params: { id: this.$path('form.id', this.item) },
          },
        },

        { title: this.$t('newInquiry') },
      ]
    },
  },
  methods: {
    ...mapActions('FormSubmit', ['fetch', 'remove']),
  },
  metaInfo() {
    return {
      title: `${this.$path('item.form.title')} - ${this.$t('new')} ${this.$tc('inquiry', 1).toLowerCase()}`,
    }
  },
  components: {
    HeaderComponent,
    Card,
    RemoveComponent,
    DataTable,
    Badge,
    FileComponent,
  },
}
</script>

<style lang="scss">
.form-submit-mutate {
  background-color: $background-grey;

  .card-v2 > .card-v2__header {
    flex-direction: row;
    justify-content: space-between;
    h2 {
      margin: 0;
    }
  }
  &__content {
  }

  &__items {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }
  &__item {
    padding: 1rem;
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    background-color: var(--lightest-grey);
    border-radius: var(--border-radius);
    border: 1px solid var(--border-color);
  }
}
</style>
